import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { LogOut, Moon, Sun } from 'lucide-react';
import { useMocks } from '@config/env';
import { emailFromUser } from '@core/auth/adminClaims';
import { signOutToLogin } from '@core/auth/signOut';
import { useTheme } from '@shared/theme';

/**
 * Header account control: the signed-in admin's email + a dropdown with a light/dark segmented
 * toggle and Log out. In mock mode the OIDC provider isn't mounted, so the email is a placeholder
 * and `useAuth()` is never called (the branch is fixed at module load via `useMocks`).
 */
export function AdminProfile() {
  if (useMocks) return <AdminProfileView email="admin@demo (mock)" />;
  return <CognitoAdminProfile />;
}

function CognitoAdminProfile() {
  const auth = useAuth();
  return <AdminProfileView email={emailFromUser(auth.user)} />;
}

function AdminProfileView({ email }: { email: string }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => { if (!rootRef.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initial = (email.trim()[0] ?? '?').toUpperCase();

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={email}
        className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 hover:bg-canvas-inset"
      >
        <span
          className="flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-semibold text-white"
          style={{ background: 'var(--accent)' }}
        >
          {initial}
        </span>
        <span className="hidden max-w-[180px] truncate text-[13px] text-text-muted sm:block">{email}</span>
      </button>

      {open && (
        <div
          role="dialog"
          className="absolute right-0 top-full z-50 mt-2 flex w-[220px] flex-col gap-1 rounded-xl p-3"
          style={{
            background: 'var(--canvas-raised)',
            border: '1px solid var(--canvas-divider)',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
          }}
        >
          <div className="flex h-10 items-center truncate rounded-lg px-3 text-[13px] text-text-muted" title={email}>
            {email}
          </div>
          <ThemeSegmented />
          <button
            type="button"
            onClick={signOutToLogin}
            className="flex h-10 items-center gap-2 rounded-lg px-2 text-[13px] text-text hover:bg-canvas-inset"
          >
            <LogOut className="h-5 w-5 text-text-muted" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function ThemeSegmented() {
  const { theme, setTheme } = useTheme();
  const activeStyle = { background: 'var(--canvas-raised)', boxShadow: '0 0 16px rgba(0, 0, 0, 0.04)' };
  return (
    <div className="flex items-center gap-1 rounded-lg p-1" style={{ background: 'var(--canvas-inset)' }}>
      <button
        type="button"
        onClick={() => setTheme('light')}
        aria-label="Light mode"
        aria-pressed={theme === 'light'}
        className="flex h-7 flex-1 items-center justify-center gap-1.5 rounded-md text-[12px] text-text-muted"
        style={theme === 'light' ? activeStyle : undefined}
      >
        <Sun className="h-4 w-4" /> Light
      </button>
      <button
        type="button"
        onClick={() => setTheme('dark')}
        aria-label="Dark mode"
        aria-pressed={theme === 'dark'}
        className="flex h-7 flex-1 items-center justify-center gap-1.5 rounded-md text-[12px] text-text-muted"
        style={theme === 'dark' ? activeStyle : undefined}
      >
        <Moon className="h-4 w-4" /> Dark
      </button>
    </div>
  );
}
